import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Zap, Upload, Check, AlertCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import { isValidPhone, normalizePhone, PHONE_ERROR } from '@/lib/phone';
import GalleryComposer, { validateGalleryPosts, galleryPostsToPayload } from '@/components/GalleryComposer';

const GENRES = ['House', 'Techno', 'EDM', 'Bollywood', 'Punjabi', 'Hip Hop', 'Afrobeats', 'Open Format', 'Other'];
const IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const IG_RE = /^https:\/\/(www\.)?instagram\.com\/.+/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Centered({ children }) { return <div className="max-w-2xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>; }
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in to apply as a DJ.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}

export default function DJApply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authed] = useState(api.isAuthenticated());
  const [loading, setLoading] = useState(true);
  const [appState, setAppState] = useState(null);   // existing application (or null = none)
  const fileRef = useRef(null);

  // form
  const [djName, setDjName] = useState('');
  const [realName, setRealName] = useState('');
  const [phone, setPhone] = useState('');
  const [publicPhone, setPublicPhone] = useState(false);
  const [city, setCity] = useState('');
  const [genres, setGenres] = useState([]);
  const [customGenre, setCustomGenre] = useState('');
  const [bio, setBio] = useState('');
  const [instagram, setInstagram] = useState('');
  const [bookingEmail, setBookingEmail] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [agree, setAgree] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [nameCheck, setNameCheck] = useState(null);   // {available, message}
  const [submitting, setSubmitting] = useState(false);
  const [galleryPosts, setGalleryPosts] = useState([]);   // DJ Gallery (Chunk 2)

  const loadStatus = async () => {
    if (!api.isAuthenticated()) { setLoading(false); return; }
    try { setAppState(await api.get('/users/me/dj-application')); }
    catch (_) { setAppState(null); }   // 404 => no application
    finally { setLoading(false); }
  };
  useEffect(() => { loadStatus(); }, []);

  // DJ-name availability (debounced)
  useEffect(() => {
    const nm = djName.trim();
    if (!nm) { setNameCheck(null); return; }
    const t = setTimeout(() => {
      api.get(`/djs/name-availability?name=${encodeURIComponent(nm)}`)
        .then((r) => setNameCheck(r)).catch(() => setNameCheck(null));
    }, 400);
    return () => clearTimeout(t);
  }, [djName]);

  const toggleGenre = (g) => setGenres((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!IMG_TYPES.includes(f.type)) { toast.error('Use a JPG, PNG, or WebP image.'); return; }
    if (f.size > MAX_BYTES) { toast.error('Image too large (max 5 MB).'); return; }
    setImagePreview(URL.createObjectURL(f));   // local preview (pending images aren't public)
    setUploading(true);
    try {
      const res = await api.uploadDJImage(f);
      setImageUrl(res.url);
      toast.success('Image uploaded');
    } catch (err) {
      setImageUrl(null);
      toast.error(err.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const resolvedGenres = () => {
    const g = genres.filter((x) => x !== 'Other');
    if (genres.includes('Other') && customGenre.trim()) g.push(customGenre.trim());
    return g;
  };

  const validate = () => {
    if (!djName.trim()) return 'DJ name is required.';
    if (nameCheck && nameCheck.available === false) return 'This DJ name is already in use.';
    if (!realName.trim()) return 'Real name is required.';
    if (!phone.trim()) return 'Phone number is required.';
    if (!isValidPhone(phone)) return PHONE_ERROR;
    if (!city.trim()) return 'City is required.';
    if (!imageUrl) return 'A profile image is required.';
    if (resolvedGenres().length === 0) return 'Select at least one genre.';
    if (genres.includes('Other') && !customGenre.trim()) return 'Enter your custom genre.';
    if (bio.trim().length < 50) return 'Bio must be at least 50 characters.';
    if (bio.trim().length > 500) return 'Bio must be at most 500 characters.';
    if (instagram.trim() && !IG_RE.test(instagram.trim())) return 'Instagram must be a valid https://instagram.com/ URL.';
    if (bookingEmail.trim() && !EMAIL_RE.test(bookingEmail.trim())) return 'Enter a valid booking email.';
    if (portfolioUrl.trim() && !/^https?:\/\/.+/i.test(portfolioUrl.trim())) return 'Portfolio link must start with http:// or https://';
    if (!agree) return 'You must accept the publishing & accuracy agreement.';
    const gErr = validateGalleryPosts(galleryPosts);   // Gallery is required (Chunk 2)
    if (gErr) return gErr;
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        dj_name: djName.trim(),
        real_name: realName.trim(),
        email: (bookingEmail.trim() || user?.email || '').trim(),
        phone: normalizePhone(phone),
        public_phone: publicPhone,
        city: city.trim(),
        profile_picture: imageUrl,
        instagram: instagram.trim() || null,
        booking_email: bookingEmail.trim() || null,
        portfolio_url: portfolioUrl.trim() || null,
        genres: resolvedGenres(),
        bio: bio.trim(),
        agreement_accepted: true,
        gallery_posts: galleryPostsToPayload(galleryPosts),   // DJ Gallery (Chunk 2)
      };
      await api.post('/djs/apply', payload);
      toast.success('Application submitted — pending review.');
      navigate('/dj-status');   // Chunk 2: go to the status page (now shows "under review")
    } catch (err2) {
      toast.error(err2.message || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;

  // ---- status routing (Chunk 2) ----
  const st = appState?.status;
  // Pending/approved users view their status on the dedicated /dj-status page.
  if (st === 'pending' || st === 'approved') return <Navigate to="/dj-status" replace />;

  // none / rejected / removed / archived -> render the blank application form.
  return (
    <div className="max-w-2xl mx-auto px-4 pt-20 pb-24 md:pb-8">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2"><Zap className="h-6 w-6" /> Apply as a DJ</h1>
      <p className="opacity-70 mb-6">Accepted DJs appear in the public directory with a shareable profile page.</p>

      {['rejected', 'removed', 'archived'].includes(st) && (
        <div className="mb-6 rounded-md border border-border/60 bg-muted/40 p-3 text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Submitting this form creates a brand-new application. Your previous one is not reused.</span>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Application</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="djn">DJ name *</Label>
              <Input id="djn" value={djName} onChange={(e) => setDjName(e.target.value)} required />
              {nameCheck && (
                <p className={`text-xs mt-1 ${nameCheck.available ? 'text-green-600' : 'text-red-600'}`}>{nameCheck.message}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="rn">Real name *</Label><Input id="rn" value={realName} onChange={(e) => setRealName(e.target.value)} required /></div>
              <div><Label htmlFor="city">City *</Label><Input id="city" value={city} onChange={(e) => setCity(e.target.value)} required /></div>
            </div>
            <div>
              <Label htmlFor="phone">Phone number *</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              <div className="flex items-center gap-2 mt-2">
                <Switch id="pp" checked={publicPhone} onCheckedChange={setPublicPhone} />
                <Label htmlFor="pp" className="text-sm opacity-80">Display my phone publicly for event bookings</Label>
              </div>
            </div>
            <div>
              <Label>Profile image *</Label>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" />
              <div className="flex items-center gap-3 mt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload className="h-4 w-4 mr-2" />{uploading ? 'Uploading…' : 'Choose image'}
                </Button>
                {imagePreview && <img src={imagePreview} alt="preview" className="h-14 w-14 rounded-md object-cover" />}
                {imageUrl && <Check className="h-4 w-4 text-green-600" />}
              </div>
              <p className="text-xs opacity-60 mt-1">JPG, PNG or WebP, max 5 MB. Cropped to a square.</p>
            </div>
            <div>
              <Label>Genres *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {GENRES.map((g) => (
                  <Badge key={g} onClick={() => toggleGenre(g)}
                    variant={genres.includes(g) ? 'default' : 'outline'}
                    className="cursor-pointer">{g}</Badge>
                ))}
              </div>
              {genres.includes('Other') && (
                <Input className="mt-2" placeholder="Your genre" value={customGenre} onChange={(e) => setCustomGenre(e.target.value)} />
              )}
            </div>
            <div>
              <Label htmlFor="bio">Bio *</Label>
              <Textarea id="bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
              <p className={`text-xs mt-1 ${bio.trim().length < 50 || bio.trim().length > 500 ? 'text-amber-600' : 'opacity-60'}`}>
                {bio.trim().length}/500 (min 50)
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label htmlFor="ig">Instagram (optional)</Label><Input id="ig" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="https://instagram.com/…" /></div>
              <div><Label htmlFor="be">Booking email (optional)</Label><Input id="be" value={bookingEmail} onChange={(e) => setBookingEmail(e.target.value)} placeholder="bookings@…" /></div>
            </div>
            <div>
              <Label htmlFor="pf">Portfolio link (optional)</Label>
              <Input id="pf" value={portfolioUrl} onChange={(e) => setPortfolioUrl(e.target.value)} placeholder="https://…" />
              <p className="text-xs opacity-60 mt-1">A link to your mixes/website (separate from gallery posts).</p>
            </div>
            <div className="flex items-start gap-2 pt-1">
              <Checkbox id="agree" checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
              <Label htmlFor="agree" className="text-sm opacity-80 font-normal">
                I confirm the information is accurate, I have permission to use the image and links, and SoundWolves may publish my profile after admin approval.
              </Label>
            </div>

            <div className="pt-2 border-t border-border/60">
              <GalleryComposer posts={galleryPosts} setPosts={setGalleryPosts} disabled={submitting} />
            </div>

            <div className="pt-2 flex justify-end">
              <Button type="submit" disabled={submitting || uploading}>{submitting ? 'Submitting…' : 'Submit application'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

